import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for scroll-triggered entrance animations using IntersectionObserver
 * @param {number} threshold - Intersection threshold (0-1)
 * @param {number} delay - Delay before showing content (ms)
 * @returns {Object} - { isReady, refs, visibleSections }
 */
export const useScrollAnimation = (threshold = 0.2, delay = 100) => {
  const [isReady, setIsReady] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const observerRef = useRef(null);
  const sectionRefs = useRef({});

  // Initialize ready state
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  // Set up intersection observer
  useEffect(() => {
    if (!isReady) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.dataset.section) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true,
            }));
          }
        });
      },
      { threshold }
    );

    // Observe all registered sections
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) {
        observerRef.current.observe(ref);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isReady, threshold]);

  // Register a section ref
  const registerRef = useCallback((sectionName) => {
    return (el) => {
      if (el) {
        sectionRefs.current[sectionName] = el;
      }
    };
  }, []);

  return {
    isReady,
    registerRef,
    visibleSections,
  };
};
